import axios from "axios";
import GLOBAL from "context/global";

// Dedicated axios instance for the AI Data Mapping backend (separate FastAPI service).
//
// Every route on that service now requires a bearer token (ESG-3973), so the interceptor below attaches
// one to all of them — the previous arrangement, where only createSession() sent a token, no longer
// works because there is no unauthenticated route left to call.
const client = axios.create({
  baseURL: process.env.REACT_APP_DATA_MAPPING_API_URL,
});

// Mirrors src/api/axios.js's request interceptor: same GLOBAL.accessToken (set by AuthProvider), and it
// only fills the header in when a caller has not set one explicitly.
//
// Deliberately WITHOUT that file's unauthorizedInterceptor, which hard-redirects to "/" on any 401. On
// this page that would eject a user out of a mapping session they were part-way through instead of
// telling them what happened; the calling components surface the error themselves.
client.interceptors.request.use((config) => {
  if (!config.headers["Authorization"] && GLOBAL.accessToken) {
    config.headers["Authorization"] = `Bearer ${GLOBAL.accessToken}`;
  }
  return config;
});

export async function createFoundationSession(foundationYearId) {
  // A Foundation Data v2 curation session: bound to a year rather than to a carbon data task, so it
  // carries no questionnaire_id and no ai_ingest_target at all. Its destination is that year's curated
  // table, which the backend describes to the agent itself — nothing about it is chosen here.
  //
  // Refused here rather than sent: String(undefined) is the truthy string "undefined", which the backend's
  // own required-field check would accept, and the result is a session bound to a year that does not exist
  // — silently useless rather than loudly broken.
  if (foundationYearId === undefined || foundationYearId === null || foundationYearId === "") {
    throw new Error("createFoundationSession needs a foundation year id");
  }
  const { data } = await client.post("/api/sessions", {
    session_kind: "foundation_v2",
    foundation_year_id: String(foundationYearId),
  });
  return data;
}

export async function listFoundationSessions(foundationYearId) {
  if (foundationYearId === undefined || foundationYearId === null || foundationYearId === "") {
    throw new Error("listFoundationSessions needs a foundation year id");
  }
  // Narrowed to the year AND to the kind, server-side. Both matter: a carbon session offered in this
  // picker would hand the user a session whose data and whose destination belong to the other feature.
  const { data } = await client.get("/api/sessions", {
    params: { session_kind: "foundation_v2", foundation_year_id: String(foundationYearId) },
  });
  return data;
}

export async function importRows(sessionId, name, rows) {
  // A push, not a pull: the caller already has these rows on screen. The response reports the frame key
  // actually used, which can differ from `name` — the backend repairs a reserved or messy name rather
  // than rejecting it, and the caller needs the real key to reference the frame afterwards.
  const { data } = await client.post(`/api/sessions/${sessionId}/import-rows`, { name, rows });
  return data;
}

/**
 * Push the datapoints this organisation asked for, as the session's read-only reference list.
 *
 * `datapoints` goes through exactly as Java returned it (`category`, `dataKey`, `unit`, `sortOrder`) — the
 * service decides the frame's column names, so there is one place that knows them and the prompt describing
 * them cannot drift from what is registered.
 *
 * Idempotent by replacement, unlike `importRows`: re-pushing is the normal case, because every resume of a
 * session does it. An organisation with no structure pushes an empty list and gets `registered: false`.
 */
export async function importTargetStructure(sessionId, datapoints) {
  const { data } = await client.post(`/api/sessions/${sessionId}/import-target-structure`, {
    datapoints,
  });
  return data;
}

/**
 * How much of the wanted structure one frame covers.
 *
 * Computed server-side against the whole frame, deliberately: `fetchDataFrameData` caps `page_size` at 500,
 * so counting here would describe a page rather than the data — and the number has to be the same claim the
 * save dialog reports, not a second estimate of it.
 */
export async function fetchFoundationCoverage(sessionId, dfKey) {
  const { data } = await client.get(`/api/sessions/${sessionId}/foundation-coverage`, {
    params: { df_key: dfKey },
  });
  return data;
}

export async function buildCuratedPayload(sessionId, { dfKey, mode, expectedReplacedRows }) {
  // Returns the request the browser then POSTs to Java. Built server-side so the frame is read once and
  // atomically — paginating it here would let a chat turn land mid-read, and the saved rows would not be
  // the rows anyone reviewed.
  const { data } = await client.post(`/api/sessions/${sessionId}/curated-payload`, {
    df_key: dfKey,
    mode,
    expected_replaced_rows: expectedReplacedRows ?? null,
  });
  return data;
}

export async function createSession(questionnaireId, aiIngestTarget) {
  // questionnaire_id is required: every session belongs to a data task (ds_session.questionnaire_id is
  // NOT NULL), which is what retires the old task-less standalone flow. The backend derives the org from
  // the token, so nothing about the tenant is sent from here.
  //
  // ai_ingest_target is optional and validated server-side against the AiIngestTarget names — an unknown
  // value is rejected rather than persisted, so a hand-edited URL cannot leave a session pointing at a
  // destination that does not exist.
  const body = { questionnaire_id: String(questionnaireId) };
  if (aiIngestTarget) {
    body.ai_ingest_target = aiIngestTarget;
  }
  const { data } = await client.post("/api/sessions", body);
  return data;
}

export async function listSessions(questionnaireId, target) {
  // Always scoped to the caller's org by the backend; narrowed to one task when an id is given, and to
  // one destination when a target is. This used to return every session in the process, for any caller.
  //
  // Task alone was too wide: a questionnaire has six ai-ingest targets, so opening AI Mapping from
  // Scope 1 Stationary offered sessions built for Mobile and for Scope 2 electricity.
  const params = {};
  if (questionnaireId) params.questionnaire_id = String(questionnaireId);
  if (target) params.target = String(target);
  const { data } = await client.get("/api/sessions", { params });
  return data;
}

export async function getSession(sessionId) {
  const { data } = await client.get(`/api/sessions/${sessionId}`);
  return data;
}

export async function deleteSession(sessionId) {
  await client.delete(`/api/sessions/${sessionId}`);
}

export async function uploadFile(sessionId, file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await client.post(`/api/sessions/${sessionId}/upload`, form);
  return data;
}

export async function importFromApi(sessionId, { url, name, token, jsonPath }) {
  const { data } = await client.post(`/api/sessions/${sessionId}/import-api`, {
    url,
    name,
    token: token || null,
    json_path: jsonPath || null,
  });
  return data;
}

export async function getMessages(sessionId) {
  const { data } = await client.get(`/api/sessions/${sessionId}/messages`);
  return data;
}

export async function fetchDataFrameList(sessionId) {
  const { data } = await client.get(`/api/sessions/${sessionId}/dataframes`);
  return data.dataframes;
}

export async function fetchDataFrameData(sessionId, dfKey, page = 1, pageSize = 100) {
  const { data } = await client.get(
    `/api/sessions/${sessionId}/dataframes/${encodeURIComponent(dfKey)}`,
    { params: { page, page_size: pageSize } }
  );
  return data;
}

// The ready-to-POST ai-ingest request for a reviewed frame, or the reasons one cannot be built.
//
// Assembled server-side rather than here, and the reason is not convenience: `GET /dataframes/{key}` caps
// page_size at 500, so building 20,000 rows in the browser is 40 requests, and a chat turn landing between
// them re-maps the frame — at which point the rows committed are not the rows anyone reviewed, and nothing
// in the result says so. This reads the frame once, atomically.
//
// `dfKey` is required on purpose: the frame the user reviewed is a choice, not something to infer.
// A `committable: false` answer comes back as 200 with an `issues` array — not an error to throw on.
export async function buildCommitPayload(sessionId, dfKey, { mode = "APPEND", expectedReplacedRows } = {}) {
  const { data } = await client.post(`/api/sessions/${sessionId}/commit-payload`, {
    df_key: dfKey,
    mode,
    expected_replaced_rows: expectedReplacedRows ?? null,
  });
  return data;
}

// No separate getTrail for the COMMIT flow: `buildCommitPayload` already returns `trail_markdown`, and it is
// the exact document the commit will carry. Fetching it from GET /trail instead would show the user a
// second render of the same thing, which could differ if the two ever drifted — the one guarantee the commit
// panel needs is that what is displayed is what is sent.
//
// getTrail below is for a DIFFERENT surface — the Working Data "History" tray, viewable any time before
// commit, with no payload to stay byte-identical to.
export async function getTrail(sessionId) {
  const { data } = await client.get(`/api/sessions/${sessionId}/trail`);
  return data;
}

export async function fetchCharts(sessionId) {
  const { data } = await client.get(`/api/sessions/${sessionId}/charts`);
  return data.charts;
}

export async function deleteChart(sessionId, chartId) {
  await client.delete(`/api/sessions/${sessionId}/charts/${encodeURIComponent(chartId)}`);
}

export async function exportDataFrame(sessionId, dfKey) {
  const response = await client.get(
    `/api/sessions/${sessionId}/dataframes/${encodeURIComponent(dfKey)}/export`,
    { responseType: "blob" }
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${dfKey}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default client;
